import { useState, useEffect } from "react";
import Button from "../../components/ui/button/Button";
import PaginatedDropdown, { mapOptions } from "../../components/ui/PaginatedDropDown";
import MultiDropdown from "../../components/common/MultiDropdown";
import { X } from "lucide-react";
import { API_BASE_URL } from "../../utilities/constants";

const AdvancedFilterForm = ({ onSubmit, onClose, initialValues, setShow }) => {
  const [formData, setFormData] = useState({
    stylistId: "",
    vibe: "",
    age: "",
    height: "",
    weight: "",
    gender: "",
  });

  const [isStyleDropdownMode, setIsStyleDropdownMode] = useState(false);

  const dropdownSources = [
    {
      id: "events",
      label: "Events",
      url: `${API_BASE_URL}/api/1.0.0/aimatchedstyle/all-categories?`,
      datakey: "",
      searchkey: "keyword",
      mapOptions: (records) => mapOptions(records, "id", (o) => `${o.displayName}`),
    },
    {
      id: "vibes",
      label: "Vibes",
      url: `${API_BASE_URL}/api/1.0.0/vibecheck/vibe-questions-choices?`,
      datakey: "data",
      searchkey: "keyword",
      mapOptions: (data) => {
        const choices = data?.[0]?.choices || [];
        return choices.map(c => ({ value: c.id, label: c.choiceText }));
      },
    },
  ];

  useEffect(() => {
    if (initialValues) {
      setFormData((prev) => ({ ...prev, ...initialValues }));
    }
  }, [initialValues]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    setShow(false); // Close the modal
  };

  const handleClear = () => {
    const cleared = {
      stylistId: "",
      vibe: "",
      age: "",
      height: "",
      weight: "",
      gender: "",
    };
    setFormData(cleared);
    onSubmit(null); // Clear filters in parent
    setShow(false);
  }

  return (
    <div className="w-full">
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
            Advanced Filter
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Filter looks by specific attributes.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            if (setShow) setShow(false);
            if (onClose) onClose();
          }}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
        >
          <X size={20} className="text-gray-500" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Stylist ID */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
              Stylist
            </label>
            <PaginatedDropdown
              searchkey="keyword"
              datakey="data"
              selectedValue={formData.stylistId ? { value: formData.stylistId, label: formData.stylistName || formData.stylistId } : null}
              url={`${API_BASE_URL}/api/1.0.0/aistylist/v2/stylists?type=VIRTUAL&`}
              mapOptions={(records) =>
                mapOptions(records, "id", (o) => `${o.displayName}`)
              }
              placeHolder="Select Stylist"
              onChange={(v) => {
                setFormData((prev) => ({ ...prev, stylistId: v?.value || "", stylistName: v?.label || "" }));
              }}
            />
          </div>

          {/* Vibe */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="text-sm font-medium text-gray-900 dark:text-white">
                Style/Events
              </label>
              <button
                type="button"
                onClick={() => setIsStyleDropdownMode(!isStyleDropdownMode)}
                className="text-xs text-brand-600 dark:text-brand-400 hover:underline focus:outline-none"
              >
                {isStyleDropdownMode ? "Switch to Manual Input" : "Select from List"}
              </button>
            </div>
            {isStyleDropdownMode ? (
              <MultiDropdown
                sources={dropdownSources}
                selectedValue={formData.vibe ? { value: formData.vibe, label: formData.vibe } : null}
                placeHolder="Select Style/Event"
                onChange={(v) => {
                  setFormData((prev) => ({ ...prev, vibe: v?.label || "" }));
                }}
              />
            ) : (
              <input
                type="text"
                name="vibe"
                value={formData.vibe}
                onChange={handleChange}
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-brand-500 focus:border-brand-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-brand-500 dark:focus:border-brand-500"
                placeholder="e.g. old money / work"
              />
            )}
          </div>

          {/* Age */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
              Age
            </label>
            <input
              type="number"
              name="age"
              value={formData.age}
              onChange={handleChange}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-brand-500 focus:border-brand-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-brand-500 dark:focus:border-brand-500"
              placeholder="e.g. 25"
            />
          </div>

          {/* Height */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
              Height (cm)
            </label>
            <input
              type="number"
              name="height"
              value={formData.height}
              onChange={handleChange}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-brand-500 focus:border-brand-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-brand-500 dark:focus:border-brand-500"
              placeholder="e.g. 180"
            />
          </div>

          {/* Weight */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
              Weight (kg)
            </label>
            <input
              type="number"
              name="weight"
              value={formData.weight}
              onChange={handleChange}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-brand-500 focus:border-brand-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-brand-500 dark:focus:border-brand-500"
              placeholder="e.g. 70"
            />
          </div>

          {/* Gender */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
              Gender
            </label>
            <select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-brand-500 focus:border-brand-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-brand-500 dark:focus:border-brand-500"
            >
              <option value="">Select Gender</option>
              <option value="MALE">Male</option>
              <option value="FEMALE">Female</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button
            variant="secondary"
            className="px-6"
            onClick={handleClear}
            type="button"
          >
            Clear
          </Button>
          <Button
            variant="primary"
            className="px-6"
            type="submit"
          >
            Apply Filter
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdvancedFilterForm;
